import React, { useState, useMemo } from 'react';
import { Container, Typography, Box, useTheme, TextField, InputAdornment, Paper, Grid, Link } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Helmet } from 'react-helmet-async';
import { USStateMap } from '../components/USStateMap';
import { stateLegislatorUrls } from '../utils/stateLegislatorUrls';
import { trackEvent } from '../components/GoogleAnalytics';

const LegislatorLookup: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and sort states alphabetically
  const filteredStates = useMemo(() => {
    const sorted = [...stateLegislatorUrls].sort((a, b) => a.state.localeCompare(b.state));

    if (!searchQuery.trim()) {
      return sorted;
    }

    const query = searchQuery.toLowerCase();
    return sorted.filter((stateData) => stateData.state.toLowerCase().includes(query));
  }, [searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim().length > 2) {
      trackEvent('state_lookup_search', 'legislator_lookup', query);
    }
  };

  const handleStateClick = (stateName: string) => {
    trackEvent('state_card_click', 'legislator_lookup', stateName);
  };

  return (
    <>
      <Helmet>
        <title>RobertGarza.us Family Court Reform - Legislator Lookup</title>
        <meta
          name="description"
          content="Find your state legislators quickly. Click your state on the interactive map to access your state's official legislator lookup tool."
        />
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
            }}
          >
            Legislator Lookup
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: 'auto',
              mb: 1,
            }}
          >
            Click your state on the map to find your legislators and contact them about family court reform.
          </Typography>
        </Box>

        <USStateMap darkMode={isDarkMode} />

        {/* Search Field */}
        <Box sx={{ mt: 6, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search states..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          {searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {filteredStates.length > 0
                ? `Found ${filteredStates.length} state${filteredStates.length !== 1 ? 's' : ''}`
                : 'No states found'}
            </Typography>
          )}
        </Box>

        {/* State Cards Grid */}
        <Grid container spacing={3}>
          {filteredStates.map((stateData) => (
            <Grid item xs={12} sm={6} md={3} key={stateData.state}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
              >
                <Link
                  href={stateData.stateUrlFindLeg}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleStateClick(stateData.state)}
                  underline="none"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  {stateData.state}
                  <OpenInNewIcon fontSize="small" />
                </Link>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export default LegislatorLookup;
